import React, { useState } from "react";
import { X, Download, Loader2, Calendar } from "lucide-react";
import { API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function SyllabusModal({ programSlug, programTitle, onClose }) {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/programs/${programSlug}/syllabus`, { name, email, phone: phone || null }, {
        responseType: "blob", withCredentials: true,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `novya-${programSlug}-syllabus.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Syllabus downloaded");
      setDone(true);
    } catch (err) {
      let msg = "Download failed";
      try {
        const text = err.response?.data ? await err.response.data.text() : "";
        const parsed = text ? JSON.parse(text) : null;
        msg = formatApiError(parsed?.detail) || msg;
      } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const goToBooking = () => {
    const qs = new URLSearchParams({ program: programSlug, name, email }).toString();
    nav(`/advisor/book?${qs}`);
  };

  return (
    <div
      data-testid="syllabus-modal"
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="bg-white text-novya-ink max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-6 py-4 border-b border-novya-border">
          <div className="font-mono text-xs tracking-[0.2em] uppercase text-novya-muted">
            {done ? "Next step" : "Download syllabus"}
          </div>
          <button onClick={onClose} data-testid="syllabus-close-btn" className="text-novya-muted hover:text-novya-ink"><X size={20} /></button>
        </div>

        {!done ? (
          <form onSubmit={submit} className="p-6 md:p-8">
            <h3 className="font-display font-black text-2xl md:text-3xl tracking-tighter leading-tight">
              The full {programTitle} curriculum.
            </h3>
            <p className="mt-3 text-sm text-novya-muted leading-relaxed">
              Drop your details and we&apos;ll send the syllabus straight to you. No spam — just the modules, outcomes, and a way to book an advisor call.
            </p>
            <div className="mt-6 space-y-5">
              <Field label="Full name" value={name} onChange={setName} required testid="syllabus-name-input" />
              <Field label="Email" type="email" value={email} onChange={setEmail} required testid="syllabus-email-input" />
              <Field label="Phone (optional)" value={phone} onChange={setPhone} testid="syllabus-phone-input" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="syllabus-submit-btn"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-novya-ink text-white font-medium hover:bg-novya-accent transition-colors disabled:opacity-50"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : <><Download size={16} /> Download PDF</>}
            </button>
          </form>
        ) : (
          <div className="p-6 md:p-8" data-testid="syllabus-done-step">
            <h3 className="font-display font-black text-2xl md:text-3xl tracking-tighter leading-tight">
              Syllabus sent.<br />Want to talk it through?
            </h3>
            <p className="mt-3 text-sm text-novya-muted leading-relaxed">
              Skip the email tag. Book a 15-minute call with a Novya advisor — they&apos;ll walk you through the curriculum, mentors, and cohort dates.
            </p>
            <button
              onClick={goToBooking}
              data-testid="syllabus-book-advisor-btn"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-novya-accent text-white font-medium hover:opacity-90 transition-opacity"
            >
              <Calendar size={16} /> Book 15-min advisor call
            </button>
            <button
              onClick={onClose}
              data-testid="syllabus-done-close-btn"
              className="mt-3 w-full py-3 border border-novya-border text-novya-muted text-sm hover:border-novya-ink hover:text-novya-ink transition-colors"
            >
              Maybe later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 w-full border-b border-novya-ink bg-transparent py-3 outline-none focus:border-novya-accent"
      />
    </label>
  );
}
